import { createSelector } from 'reselect';
import { fromChartSelectors } from '@mode/charts/domain';
import { SwitchSchemaModel, SwitchRenderModel, EncodingHelpers, makeSummaryData } from '@mode/charts/util-vegas';
import { AppState } from '@mode/legacy/types-app-state';
import { ReportLayoutSelectors } from './report-layout.selectors';
import { SelectedReportSelectors } from './selected-report.selectors';
import { fromFlamingoSelectors } from '@mode/switch-views/domain';

export namespace ChartSelectors {
  export const getWatchedCharts = createSelector(
    (state: AppState) => state.chart.tokens,
    (state: AppState) => state.chart.versions,
    ReportLayoutSelectors.chartTokensInLayout,
    (chartTokens, versions, layoutTokens) =>
      chartTokens.filter((c) => versions.get(c) === 2 && layoutTokens.indexOf(c) >= 0).toArray()
  );

  export function createIsValidSelector(chartToken: string) {
    return createSelector(
      (state: AppState) => fromChartSelectors.selectChartTypes(state).get(chartToken),
      fromChartSelectors.createEncodingSelector(chartToken),
      fromChartSelectors.createQueryTokenSelector(chartToken),
      SelectedReportSelectors.getModel,
      (type, encoding, queryToken, model) => {
        let isValid: boolean;

        switch (type) {
          case 'table':
            isValid =
              EncodingHelpers.validateTableEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length <=
              0;
            break;
          default:
            isValid =
              EncodingHelpers.validateChartEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length <=
              0;
            break;
        }

        return isValid;
      }
    );
  }

  export function createRenderDataModelSelector(chartToken: string, planId: string) {
    return createSelector(
      fromFlamingoSelectors.createPlanSelector(planId),
      fromFlamingoSelectors.createResultSelector(planId),
      SelectedReportSelectors.getModel,
      fromChartSelectors.createQueryTokenSelector(chartToken),
      (plan, result, model, queryToken) => {
        if (result != null && queryToken != null) {
          return SwitchRenderModel.make(model, queryToken, plan, result);
        }

        return null;
      }
    );
  }

  export function createFlamingoChartSummarySelector(token: string, planId: string) {
    return createSelector(
      fromChartSelectors.createChartTypeEncodingSelector(token),
      fromChartSelectors.createChartFormatSelector(token),
      fromChartSelectors.createQueryTokenSelector(token),
      SelectedReportSelectors.getModel,
      fromFlamingoSelectors.createPlanSelector(planId),
      fromFlamingoSelectors.createLoadingSelector(planId),
      fromFlamingoSelectors.createResultSelector(planId),
      (typeEncoding, format, queryToken, model, plan, loading, result) => {
        if (typeEncoding != null) {
          const { encoding, chartType } = typeEncoding;
          if (chartType !== 'table') {
            const isValid =
              EncodingHelpers.isChartEncodingComplete(encoding, chartType) &&
              EncodingHelpers.validateChartEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length <=
                0;

            if (isValid && !loading) {
              if (model != null && queryToken != null && plan != null && result != null) {
                const renderModel = SwitchRenderModel.make(model, queryToken, plan, result);
                if (renderModel != null) {
                  return makeSummaryData(renderModel, encoding);
                }
              }

              return null; // null means we expected data, but there was none, i.e. invalid chart or plan failed
            }
          }
        }

        return undefined;
      }
    );
  }
}
